import { Button, Divider, Group, Stack, Text, TextInput } from "@mantine/core";
import { useField } from "@mantine/form";
import { modals } from "@mantine/modals";
import dayjs from "dayjs";
import { useState } from "react";
import { optInGqlx } from "shared/api/gqlx";
import { useOptInUserFormContext } from "shared/components/opt-in/context";
import { useFormState } from "shared/hooks/useFormState";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { runAsync } from "shared/utils/fns";
import { some } from "shared/utils/maybe";
import { zx } from "shared/utils/zx";
import { graphql } from "src/gql";
import { Cm } from "src/gql/graphql";
import { match, P } from "ts-pattern";

export const CreateEudPanel = (props: { type: Cm; onCreated?: (id: string) => void }) => {
    const form = useOptInUserFormContext();
    const [userId, setUserId] = useFormState(form, "id");
    const [customer] = useFormState(form, "customer");
    const [name] = useFormState(form, "name");
    const [durationDays] = useFormState(form, "durationDays");
    const [language] = useFormState(form, "language");

    const [createEudIsLoading, setCreateEudIsLoading] = useState(false);

    const createOptInAccount = useGraphqlMutation(
        graphql(`
            mutation CreateOptInAccount($customerId: UUID!, $expiration: Instant, $language: Language!, $name: String) {
                createOptInUser(params: { customerId: $customerId, expiration: $expiration, language: $language, name: $name }) {
                    id
                }
            }
        `),
    );

    const value = useField({ initialValue: "" });

    async function getUserId() {
        return match(userId)
            .with(P.string, (it) => it)
            .with(P.nullish, async () => {
                const expiration = some(durationDays)
                    .if((it) => it != "forever")
                    ?.take((it) => zx.instant().parse(dayjs().add(+it, "days")));

                const { createOptInUser: user } = await createOptInAccount.mutateAsync({
                    name,
                    expiration,
                    language,
                    customerId: customer.id,
                });

                setUserId(user.id);
                return user.id;
            })
            .exhaustive();
    }

    return (
        <form
            onSubmit={(e) => {
                e.preventDefault();
                runAsync(async () => {
                    const id = await getUserId();

                    setCreateEudIsLoading(true);
                    const { addEUD } = await optInGqlx(id)(
                        graphql(`
                            mutation OptInCreateEUD($userId: UUID!, $cm: CM!, $value: String!) {
                                addEUD(userId: $userId, eudParams: { type: $cm, value: $value }) {
                                    ...EudLike
                                }
                            }
                        `),
                        { value: value.getValue(), cm: props.type, userId: id },
                    );
                    setCreateEudIsLoading(false);

                    form.setFieldValue("euds", (prev) => prev.concat(addEUD));
                    props.onCreated?.(addEUD.id);
                });
            }}
        >
            <Stack>
                <Text size="xs" c="dimmed">
                    Please enter your{" "}
                    {match(props.type)
                        .with(Cm.Email, () => "email")
                        .with(Cm.Sms, () => "phone number")
                        .otherwise(() => "device")}
                    .
                </Text>

                <TextInput {...value.getInputProps()} autoFocus />
                <Group justify="flex-end">
                    <Divider mx="-md" />
                    <Button variant="default" onClick={() => modals.closeAll()}>
                        Cancel
                    </Button>
                    <Button type="submit" disabled={value.getValue().trim().length == 0} loading={createEudIsLoading}>
                        Continue
                    </Button>
                </Group>
            </Stack>
        </form>
    );
};
